import { useQuery } from '@apollo/client';
import {
  Arrange,
  Button,
  Container,
  Split,
  SplitSection,
  Text,
  TextButton,
} from '@loomhq/lens';
import gql from 'graphql-tag';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { openModal } from 'actions/modals';
import { DATA_RETENTION_KEEP } from 'constants/dataRetention';
import {
  CLICKED_DISMISS_NOTIFICATIONS,
  CLICKED_SHARE_WITH_TEAM,
  DATA_RETENTION_BANNER_PUBLISH_CLICKED,
} from 'constants/events';
import { ROLLOUT_DATA_RETENTION_CIRCUIT_BREAKER } from 'constants/featureFlag';
import {
  SPECIAL_FOLDER_ID_TEAM_VIDEOS,
  TEAM_VIDEOS_FOLDER_NAME,
} from 'constants/folders';
import { SHARE_UI_MODAL } from 'constants/modalTypes';
import { WORKSPACE_DATA_RETENTION_READ } from 'constants/scopes';
import { SHOW_DATA_RETENTION_WARNING } from 'constants/triggers';
import { VideoVisibility } from 'constants/visibility';
import { TEAM_WORKSPACE } from 'constants/workspaces';
import { moveVideoCreator } from 'creators/shareVideo';
import { completeTriggerCreator } from 'creators/triggers';
import { selectVideoBelongsToTeam } from 'selectors/shareVideo';
import * as analytics from 'utilities/analytics';
import * as logger from 'utilities/loggerx';

import useDestination from 'hooks/useDestination';

import FeatureFlaggedComponent from 'components/feature-flag-wrapper';
import Scopes from 'components/scopes';

import styles from './styles.module.less';

const getDataRetention = gql`
  query DataRetention {
    dataRetention {
      ... on DataRetention {
        enabled
        interval
        intervalCount
        keep
      }

      ... on Error {
        message
      }
    }
  }
`;

const SharePageDataRetentionBannerInner = ({ video }) => {
  const isDestination = useDestination();
  const dispatch = useDispatch();

  const isTeamVideo = useSelector(selectVideoBelongsToTeam);
  const isPublishedVideo = video.visibility === VideoVisibility.WORKSPACE;

  const { loading, data } = useQuery(getDataRetention, {
    fetchPolicy: 'no-cache',
    onError: error => {
      logger.error(error, {
        message: 'failed fetching data retention settings',
      });
    },
  });

  const dataRetentionConfig = data?.dataRetention;

  if (
    loading || // setting is still loading
    (!isDestination && isTeamVideo) || // is team video (variant disabled)
    (isDestination && isPublishedVideo) || // is published video (variant enabled)
    !dataRetentionConfig // there's no data config
  ) {
    return null;
  }

  // Note: if admins do not elect the Team library to keep the videos, then
  // there's no point in showing the banner.
  if (
    !dataRetentionConfig.enabled ||
    (!isDestination &&
      !(dataRetentionConfig?.keep || []).includes(DATA_RETENTION_KEEP.TEAM)) ||
    (isDestination &&
      !(dataRetentionConfig?.keep || []).includes(
        DATA_RETENTION_KEEP.PUBLISHED
      ))
  ) {
    return null;
  }

  const period = `${dataRetentionConfig.intervalCount} ${dataRetentionConfig.interval}`;

  const onClickMove = () => {
    analytics.track(CLICKED_SHARE_WITH_TEAM);
    dispatch(
      moveVideoCreator(
        SPECIAL_FOLDER_ID_TEAM_VIDEOS,
        TEAM_VIDEOS_FOLDER_NAME,
        false,
        TEAM_WORKSPACE
      )
    );
    dispatch(completeTriggerCreator(SHOW_DATA_RETENTION_WARNING));
  };

  const onClickPublish = () => {
    analytics.track(DATA_RETENTION_BANNER_PUBLISH_CLICKED);
    dispatch(openModal(SHARE_UI_MODAL));
    dispatch(completeTriggerCreator(SHOW_DATA_RETENTION_WARNING));
  };

  const onClickDismiss = () => {
    // Fade out and then fire action
    dispatch(completeTriggerCreator(SHOW_DATA_RETENTION_WARNING));

    analytics.track(CLICKED_DISMISS_NOTIFICATIONS, {
      location: 'library',
    });
  };

  return (
    <>
      <Container
        radius="medium"
        padding="large"
        backgroundColor="offWhite"
        className={styles.dataRetentionBanner}
      >
        <Arrange
          justifyContent="space-between"
          gap="medium"
          autoFlow={{ default: 'row', small: 'column' }}
        >
          <Split gap={'medium'}>
            <SplitSection>
              <div className={styles.hideWave}>
                <Text size={'xxlarge'}>👋</Text>
              </div>
            </SplitSection>
            <SplitSection>
              {isDestination ? (
                <Text>
                  This is a reminder that all <strong>unpublished</strong>{' '}
                  content will be deleted after <strong>{period}</strong>.
                </Text>
              ) : (
                <Text>
                  This is a reminder that all content that is not stored in the{' '}
                  <strong>Team Library</strong> will be deleted after{' '}
                  <strong>{period}</strong> after creation.
                </Text>
              )}
            </SplitSection>
          </Split>
          <Arrange gap="small">
            {isDestination ? (
              <Button onClick={() => onClickPublish()}>Publish video</Button>
            ) : (
              <Button onClick={() => onClickMove()}>
                Move to Team Library
              </Button>
            )}
            <TextButton onClick={() => onClickDismiss()}>Dismiss</TextButton>
          </Arrange>
        </Arrange>
      </Container>
    </>
  );
};

export const SharePageDataRetentionBanner = ({ video }) => (
  <FeatureFlaggedComponent
    flag={ROLLOUT_DATA_RETENTION_CIRCUIT_BREAKER}
    activationValues={[false]}
  >
    <Scopes name={WORKSPACE_DATA_RETENTION_READ}>
      <SharePageDataRetentionBannerInner video={video} />
    </Scopes>
  </FeatureFlaggedComponent>
);
